import type { SettingRange, SettingsCardRange, SettingValue } from './infra_settings_rollout.service'

/**
 * Draft state of the settings card's range editor (PLAN-20261013 slice 08d).
 * It only holds what staff type and names the settings whose range changed;
 * the server checks every range against the catalogue and refuses one it does
 * not accept.
 */
export interface ValueDraft {
  amount: string
  unlimited: boolean
}

export interface RangeDraft {
  useDefault: boolean
  minimum: ValueDraft
  maximum: ValueDraft
}

export type RangeDrafts = Record<string, RangeDraft>

const wholeNumber = /^-?\d+$/

function valueDraft(value: SettingValue): ValueDraft {
  return value.unlimited ? { amount: '', unlimited: true } : { amount: String(value.amount ?? ''), unlimited: false }
}

function sameValue(left: SettingValue, right: SettingValue): boolean {
  return (left.unlimited === true) === (right.unlimited === true) && (left.unlimited === true || left.amount === right.amount)
}

/** The drafts that show each setting's current range, own or default. */
export function draftsFromRanges(ranges: readonly SettingsCardRange[]): RangeDrafts {
  const drafts: RangeDrafts = {}
  for (const range of ranges) {
    const shown = range.own ?? range.default
    drafts[range.key] = { useDefault: range.own === null, minimum: valueDraft(shown.minimum), maximum: valueDraft(shown.maximum) }
  }
  return drafts
}

/** True when a typed amount is not a whole number. */
export function isValueDraftInvalid(draft: ValueDraft): boolean {
  return !draft.unlimited && !wholeNumber.test(draft.amount.trim())
}

function valueFromDraft(draft: ValueDraft): SettingValue {
  return draft.unlimited ? { unlimited: true } : { amount: Number(draft.amount.trim()) }
}

export interface RangeChanges {
  /** The settings whose range changes: a range, or null for the default. */
  changes: Record<string, SettingRange | null>
  /** True while a typed amount is not a whole number. */
  invalid: boolean
}

/** The ranges the drafts change, compared with what the server sent. */
export function rangeChanges(ranges: readonly SettingsCardRange[], drafts: RangeDrafts): RangeChanges {
  const changes: Record<string, SettingRange | null> = {}
  let invalid = false
  for (const range of ranges) {
    const draft = drafts[range.key]
    if (!draft) continue
    if (draft.useDefault) {
      if (range.own !== null) changes[range.key] = null
      continue
    }
    if (isValueDraftInvalid(draft.minimum) || isValueDraftInvalid(draft.maximum)) {
      invalid = true
      continue
    }
    const next: SettingRange = { minimum: valueFromDraft(draft.minimum), maximum: valueFromDraft(draft.maximum) }
    if (range.own === null || !sameValue(range.own.minimum, next.minimum) || !sameValue(range.own.maximum, next.maximum)) {
      changes[range.key] = next
    }
  }
  return { changes, invalid }
}

/** A setting value as text in its unit, as the server names the unit. */
export function valueText(value: SettingValue, unit: string, unlimitedText: string): string {
  return value.unlimited ? unlimitedText : `${value.amount ?? ''} ${unit}`
}
